import { useState, useCallback, useEffect, useRef } from "react";
import { useParams, Link, useSearchParams } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Pencil, Trash2, Bell, Plus, Mail, MoreHorizontal, Users } from "lucide-react";
import { tripsApi, alertsApi, stewardApi } from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { tripStatus } from "@/components/trips/tripStatusView";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import LoadingState from "@/components/shared/LoadingState";
import ErrorState from "@/components/shared/ErrorState";
import EmptyState from "@/components/shared/EmptyState";
import DeleteTripDialog from "@/components/trips/DeleteTripDialog";
import CreateAlertDialog from "@/components/trips/CreateAlertDialog";
import EmailPreviewDialog from "@/components/trips/EmailPreviewDialog";
import AlertHistoryView from "@/components/trips/AlertHistoryView";
import TripScheduleControls from "@/components/trips/TripScheduleControls";
import SearchLogView from "@/components/trips/SearchLogView";
import CheckPricesButton from "@/components/trips/CheckPricesButton";
import CompanionStrip from "@/components/trips/CompanionStrip";
import TripBriefing from "@/components/trips/TripBriefing";
import TravelGroupPanel from "@/components/trips/TravelGroupPanel";
import TripTimeline, {
  type UnifiedAddStopRequest,
} from "@/components/trips/unified/TripTimeline";
import { ITEM_TYPES } from "@/components/trips/itinerary/itinerary-utils";
import StewardWorkspace, {
  type StewardSeed,
} from "@/components/trips/steward/StewardWorkspace";
import StewardGapBanner from "@/components/trips/steward/StewardGapBanner";
import TripCalendarStrip from "@/components/trips/calendar/TripCalendarStrip";
import StewardBackboneCard from "@/components/trips/steward/StewardBackboneCard";
import type { Route, Alert, ItineraryItemType } from "@/lib/types";

/* Lightweight info about a selected flight. The unified itinerary no longer
   uses optimistic local selections, but the (now-unmounted) plan components
   still import this type, so it stays exported to keep their imports valid. */
export interface FlightInfo {
  flightId: string;
  price: number;
  airline: string;
  flightNumber: string;
  departureTime?: string;
  arrivalTime?: string;
  stops?: number;
  durationMinutes?: number;
}

export default function TripDetail() {
  const { id } = useParams<{ id: string }>();
  const queryClient = useQueryClient();
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [createAlertOpen, setCreateAlertOpen] = useState(false);
  const [emailPreviewOpen, setEmailPreviewOpen] = useState(false);
  const [groupSetupOpen, setGroupSetupOpen] = useState(false);
  const [descExpanded, setDescExpanded] = useState(false);

  // When we arrive here straight from creating a trip, the server has already
  // started a fare check and handed us its jobId via ?check=…. Capture it once
  // so the Check Prices button mounts already polling — fares appear ASAP — then
  // strip the param so a reload doesn't resurrect a stale job.
  //
  // Email deep-links supply ?leg=<routeId> or ?view=fares. Capture once on
  // mount, strip them from the URL, then scroll after trip data loads (see the
  // effect below that depends on `trip`).
  const [searchParams, setSearchParams] = useSearchParams();
  const [autoCheckJobId] = useState<string | null>(() => searchParams.get("check"));
  const [deepLinkLeg] = useState<string | null>(() => searchParams.get("leg"));
  const [deepLinkView] = useState<string | null>(() => searchParams.get("view"));

  // Phase-4 email deep-link: /trips/:id?addStop=1&after=<outbound|return>&date=<YYYY-MM-DD>&type=<…>
  // Capture once on mount; we scroll to + auto-open the add dialog after trip data
  // loads, then strip the params (replace:true). `after` is captured for parity
  // with the email scheme; the actionable bits are `date` (day to prefill) + `type`.
  const [addStopRequest, setAddStopRequest] = useState<UnifiedAddStopRequest | null>(
    () => {
      if (searchParams.get("addStop") !== "1") return null;
      const date = searchParams.get("date") || undefined;
      const rawType = searchParams.get("type");
      const type =
        rawType && (ITEM_TYPES as string[]).includes(rawType)
          ? (rawType as ItineraryItemType)
          : undefined;
      return { date, type };
    },
  );

  // Steward deep link: /trips/:id?steward=1 (same capture-once pattern as
  // ?addStop=1) so emails and the completeness coach's lodging nudge can land
  // the traveler straight in the workspace.
  const [stewardOpen, setStewardOpen] = useState(
    () => searchParams.get("steward") === "1",
  );
  const [stewardSeed, setStewardSeed] = useState<StewardSeed | null>(null);

  useEffect(() => {
    const next = new URLSearchParams(searchParams);
    let changed = false;
    if (next.has("steward")) { next.delete("steward"); changed = true; }
    if (next.has("check")) { next.delete("check"); changed = true; }
    if (next.has("leg"))   { next.delete("leg");   changed = true; }
    if (next.has("view"))  { next.delete("view");  changed = true; }
    if (next.has("addStop")) { next.delete("addStop"); changed = true; }
    if (next.has("after"))   { next.delete("after");   changed = true; }
    if (next.has("date"))    { next.delete("date");    changed = true; }
    if (next.has("type"))    { next.delete("type");    changed = true; }
    if (changed) setSearchParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ["trip", id],
    queryFn: () => tripsApi.getById(id!),
    enabled: !!id,
  });

  const { data: alertsData } = useQuery({
    queryKey: ["alerts", id],
    queryFn: () => alertsApi.getByTrip(id!),
    enabled: !!id,
  });

  // Steward's opener. Cached server-side on the conversation row, so this is a
  // cheap read after the first visit. `opener: null` = nothing worth saying =
  // no banner at all; the query never blocks or errors the page.
  const { data: steward } = useQuery({
    queryKey: ["stewardOpener", id],
    queryFn: () => stewardApi.opener(id!),
    enabled: !!id,
    staleTime: 5 * 60 * 1000,
    retry: false,
  });

  const openSteward = useCallback((seed?: StewardSeed) => {
    setStewardSeed(seed ?? null);
    setStewardOpen(true);
  }, []);

  /** The gap banner's chips are bare sentences, with no add-slot behind them. */
  const openStewardWith = useCallback(
    (message?: string) => openSteward(message ? { message } : undefined),
    [openSteward],
  );

  const trip = data?.trip;

  // Scroll target — the unified itinerary surface.
  const planRef = useRef<HTMLDivElement | null>(null);

  // After a Check Prices run completes, pull fresh data so the unified
  // itinerary reflects the new fares without a manual reload. The plan surface
  // reads ["tripPlan", id]; we also refresh ["trip"] and every route's flights /
  // recommendation / focus set (other surfaces + the plan's matrix all derive
  // from these).
  const handleCheckComplete = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ["trip", id] });
    queryClient.invalidateQueries({ queryKey: ["tripPlan", id] });
    const tripRoutes: Route[] = data?.trip?.routes || [];
    tripRoutes.forEach((route: Route) => {
      queryClient.invalidateQueries({ queryKey: ["flights", route.id] });
      queryClient.invalidateQueries({ queryKey: ["recommendation", route.id] });
      queryClient.invalidateQueries({ queryKey: ["focusSet", route.id] });
    });
  }, [queryClient, id, data]);

  // Deep-link scroll for ?addStop=1: once trip data resolves, bring the timeline
  // into view. (?leg=<routeId> / ?view=fares scroll to the flight row itself,
  // inside TripTimeline, because only it knows when that row exists.)
  useEffect(() => {
    if (!trip || !addStopRequest) return;
    const timer = setTimeout(() => {
      document.getElementById("unified-itinerary")?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 150);
    return () => clearTimeout(timer);
  }, [trip, addStopRequest]);

  if (isLoading) return <LoadingState message="Loading trip..." />;
  if (error) return <ErrorState message="Failed to load trip" onRetry={() => refetch()} />;
  if (!trip) return <ErrorState message="Trip not found" />;

  const alerts: Alert[] = alertsData?.alerts || trip.alerts || [];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div className="space-y-1">
          <div className="flex items-center gap-3">
            <h1 className="text-2xl font-bold tracking-tight">{trip.name}</h1>
            {/* One rule, one place (tripStatusView): finished travel reads
                "Past", never the Active/Paused watch switch. */}
            <Badge
              title={tripStatus(trip).title}
              className={
                tripStatus(trip).tone === "active"
                  ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                  : "bg-muted text-muted-foreground border-border"
              }
            >
              {tripStatus(trip).label}
            </Badge>
          </div>
          {trip.description && (
            <div>
              {/* Two lines, then "more": the description is context, and a long
                  one pushed the first flight off the first phone screen. */}
              <p className={"text-muted-foreground " + (descExpanded ? "" : "line-clamp-2")}>
                {trip.description}
              </p>
              {trip.description.length > 140 && (
                <button
                  type="button"
                  onClick={() => setDescExpanded((v) => !v)}
                  className="mt-0.5 text-xs font-medium text-[hsl(var(--primary))] hover:underline"
                >
                  {descExpanded ? "less" : "more"}
                </button>
              )}
            </div>
          )}
        </div>
        <div className="flex items-start gap-2">
          <CheckPricesButton tripId={id!} onComplete={handleCheckComplete} autoJobId={autoCheckJobId} />
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="icon" aria-label="More trip actions" className="shrink-0">
                <MoreHorizontal className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onSelect={() => setEmailPreviewOpen(true)}>
                <Mail className="h-4 w-4" /> Preview email
              </DropdownMenuItem>
              <DropdownMenuItem asChild>
                <Link to={"/trips/" + id + "/edit"}>
                  <Pencil className="h-4 w-4" /> Edit trip
                </Link>
              </DropdownMenuItem>
              <DropdownMenuItem onSelect={() => setGroupSetupOpen(true)}>
                <Users className="h-4 w-4" /> Travel group
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                className="text-destructive focus:text-destructive"
                onSelect={() => setDeleteOpen(true)}
              >
                <Trash2 className="h-4 w-4" /> Delete trip
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      {/* Which weekday is the flight, which is the event. Flights come from the
          trip's routes; event days from itinerary items. Renders nothing when
          the trip has no dated legs or events. */}
      <TripCalendarStrip
        tripId={id!}
        routes={trip.routes || []}
        description={trip.description}
        onAddEvent={() => {
          setAddStopRequest({ type: "event" });
          document.getElementById("unified-itinerary")?.scrollIntoView({ behavior: "smooth", block: "start" });
        }}
      />

      {/* Steward's proactive hook: the unplanned stretch between the trip's
          anchors (e.g. the days between disembarking and the flight home).
          Renders ONLY when the server returned an opener — no gap, no banner. */}
      {steward?.opener && (
        <StewardGapBanner
          opener={steward.opener}
          chips={steward.chips}
          gap={steward.gap}
          changes={steward.changes}
          onOpen={openStewardWith}
          onSkipAsk={
            steward.ask
              ? () => {
                  void stewardApi
                    .skipAsk(id!)
                    .finally(() => queryClient.invalidateQueries({ queryKey: ["stewardOpener", id] }));
                }
              : undefined
          }
        />
      )}

      {/* The trip the traveler ALREADY described — the cruise, the pier-to-airport
          move its disembark date implies, the shoulder day before sailing — offered
          as itinerary rows. Sits directly under the gap banner because it is the
          same conversation: the banner counts the unplanned days, this writes down
          the planned ones. Renders nothing when there is nothing left to offer. */}
      <StewardBackboneCard tripId={id!} />

      {/* The trip as ONE timeline: flights are the backbone, each with a single
          "Flight options" control; the stretches between them hold everything
          else. id="unified-itinerary" anchors ?addStop deep links. */}
      <div id="unified-itinerary" ref={planRef}>
        <TripTimeline
          tripId={id!}
          description={trip.description}
          openOptions={deepLinkLeg || (deepLinkView === "fares" ? "first" : null)}
          addStopRequest={addStopRequest}
          onAddStopConsumed={() => setAddStopRequest(null)}
          onAskSteward={openSteward}
          stewardGap={steward?.gap}
        />
      </div>

      {/* Companions, travel group and briefing: context about the trip, below
          the trip itself. Each renders nothing when it has nothing to say. */}
      <CompanionStrip tripId={id!} description={trip.description} />
      <TravelGroupPanel tripId={id!} tripName={trip.name} showSetup={groupSetupOpen} />
      <TripBriefing tripId={id!} />

      {/* Schedule Controls - collapsed by default */}
      <TripScheduleControls
        tripId={id!}
        active={trip.active}
        checkIntervalHours={trip.check_interval_hours || 8}
        emailFrequency={trip.email_frequency || "always"}
        past={tripStatus(trip).past}
        travelEndDate={trip.travel_end_date ?? null}
      />


      <Separator />

      {/* Alerts */}
      <section className="space-y-4">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold">Alerts</h2>
          <Button variant="outline" size="sm" onClick={() => setCreateAlertOpen(true)}>
            <Plus className="h-4 w-4 mr-1.5" /> Add Alert
          </Button>
        </div>
        {alerts.length === 0 ? (
          <EmptyState icon={Bell} title="No alerts configured" description="Add alerts to get notified of price changes" />
        ) : (
          <AlertHistoryView tripId={id!} />
        )}
      </section>

      <Separator />
      <SearchLogView tripId={id!} />

      {/* The workspace itself — a right-side sheet, opened from the banner, the
          AddStopMenu "Ask Steward" row, or the ?steward=1 deep link. */}
      <StewardWorkspace
        open={stewardOpen}
        onOpenChange={setStewardOpen}
        tripId={id!}
        tripName={trip.name}
        opener={steward?.opener}
        openerChips={steward?.chips}
        conversationId={steward?.conversationId}
        seed={stewardSeed}
        onSeedConsumed={() => setStewardSeed(null)}
      />

      <DeleteTripDialog tripId={id!} tripName={trip.name} open={deleteOpen} onOpenChange={setDeleteOpen} />
      <CreateAlertDialog tripId={id!} open={createAlertOpen} onOpenChange={setCreateAlertOpen} />
      <EmailPreviewDialog tripId={id!} open={emailPreviewOpen} onOpenChange={setEmailPreviewOpen} />
    </div>
  );
}
